import {
  Box,
  Button,
  Typography,
  RadioGroup,
  Radio,
  FormControlLabel,
  CircularProgress,
  Popper,
  ListItem,
  TextField,
  Autocomplete
} from '@mui/material';
import { useEffect, useState, useCallback } from 'react';
import {
  addLocalSEOTag,
  createLocation,
  getBusinessCategory,
  getLocationDetail,
  scanCitations
} from 'src/common/Scraper';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  BusinessProfileAutocomplete,
  useBusinessProfileAutocomplete
} from 'src/components/BusinessProfileAutocomplete';
import { CitationBuilderSx } from '../styles';
import { useNavigate, useLocation } from 'react-router';
import { GoogleBusinessProfileSelector } from 'src/components/GoogleBusinessProfileSelector';
import { useGoogleBusinessProfile } from 'src/components/GoogleBusinessProfileSelector/GoogleBusinessProfileProvider';
import { useCitation } from '../Providers/CitationProvider';
import {
  setAutoSelectedCategoryId,
  setBusinessNameSelectedValue,
  setIsConnectionFlow,
  setSelectedBusinessAccountId
} from 'src/redux/localSeo';
import CommonModal from 'src/common/config/CommonModal';
import { AddLocationDetailsModal } from 'src/content/localSeo/AddLocationDetailsComponents/AddLocationDetailsModal';
import { Country } from 'country-state-city';
import {
  iso2ToIso3,
  normalize
} from 'src/content/localSeo/AddLocationDetailsComponents/funtions';
import { MapRankCss } from 'src/content/mapRankTracker/MapRankCss';
import {
  labelTypographySx,
  LocationDetailsModalSx
} from 'src/content/localSeo/AddLocationDetailsComponents/styles';
import { MapRankTexts } from 'src/content/mapRankTracker/MapRankTexts';
import { CustomTooltip } from 'src/content/keywordResearch/CustomTooltip';
import { keywordResearchDashBoardCss } from 'src/content/keywordResearch/keywordResearchCSS';
import { LocationDetailsModalTexts } from 'src/content/localSeo/AddLocationDetailsComponents/texts';
import { KeywordOverviewResultCardsCss } from 'src/content/keywordResearch/keywordOverview/keywordOverviewCSS';
import HelpIcon from '@mui/icons-material/Help';
import { BusinessProfileAutocompleteStyles } from 'src/components/BusinessProfileAutocomplete/BusinessProfileAutocomplete.styles';
import { ConnectBusinessProfileModal } from 'src/content/localSeo/ConnectBusinessProfileModal';
import { setLocalSeoSection } from 'src/redux/keywordResearch';
import { paths } from 'src/common/config/TextMessage';
import { RootState } from 'src/redux/store';

export const SelectLocationForCitation = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState<boolean>(false);
  const {
    state: googleBusinessProfileState,
    loading: googleBusinessProfileLoading
  } = useGoogleBusinessProfile();
  const [locationType, setLocationType] = useState<'existing' | 'new'>(
    googleBusinessProfileState?.allBusinessProfiles?.length > 0
      ? 'existing'
      : 'new'
  );
  const [openAddLocationDetailsModal, setOpenAddLocationDetailsModal] =
    useState<boolean>(false);
  const [showServiceArea, setShowServiceArea] = useState(true);
  const [businessCategory, setBusinessCategory] = useState(null);
  const [allFieldData, setAllFieldData]: any = useState(null);
  const [showAdditionStep, setShowAdditionStep] = useState(false);
  const [countrySelected, setCountrySelected] = useState(null);
  const [checkingFieldProgress, setCheckingFieldProgress] = useState(true);
  const [businessCategories, setBusinessCategories] = useState(null);
  const [showCategory, setShowCategory] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [showCategoryField, setShowCategoryField] = useState(false);
  const [open, setOpen] = useState(false);
  const [categoryInputValue, setCategoryInputValue] = useState('');
  const [googleAuthText, setGoogleAuth] = useState(null);
  const allowedCountries = ['US', 'GB', 'CA', 'AU', 'NZ'];
  let selectedBusinessAccountId: any = useSelector(
    (state: RootState) => state.localSeo.selectedBusinessAccountId
  );
  const {
    resetBusinessProfileAutocomplete,
    selectedOption,
    inputValue,
    loading: businessProfileLoading
  } = useBusinessProfileAutocomplete();

  useEffect(() => {
    const seoSpaceChannel = new BroadcastChannel('SEO_Space');
    seoSpaceChannel.onmessage = (event) => {
      if (event?.data?.type === 'gbpc_auth_connection') {
        dispatch(setSelectedBusinessAccountId(event?.data?.accountId));
        setGoogleAuth(event.data);
      }
    };

    return () => {
      seoSpaceChannel.close();
    };
  }, [dispatch]);


  useEffect(() => {
    if (
      googleAuthText?.message &&
      googleAuthText?.success &&
      location.pathname === paths?.citation &&
      selectedBusinessAccountId
    ) {
      dispatch(setIsConnectionFlow(true));
      dispatch(setLocalSeoSection(1));
      navigate(paths?.localChecker);
      // Clear the authentication message after 5 seconds
      setTimeout(() => {
        setGoogleAuth(null);
      }, 5000);
    }
  }, [googleAuthText, location.pathname, selectedBusinessAccountId, dispatch, navigate]);

  const {
    setSelectedBusinessProfile,
    persistStateToStorage,
    setCampaignDetails,
    setBLLocationId,
    setError
  } = useCitation();

  const clearCategoryDetails = useCallback(() => {
    setShowCategoryField(false);
    setBusinessCategories(null);
    setSelectedCategoryId(null);
    dispatch(setAutoSelectedCategoryId(null));
  }, [dispatch]);

  useEffect(() => {
    if (selectedOption?.placeId) {
      clearCategoryDetails();
      getLocationDetailByPlaceID(selectedOption?.placeId);
    }
  }, [selectedOption, clearCategoryDetails]);

  const handleLocationTypeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newType = event.target.value as 'existing' | 'new';
    setLocationType(newType);
    if (newType === 'existing') {
      clearCategoryDetails();
      resetBusinessProfileAutocomplete();
    }
  };

  const getMailChimpCall = async () => {
    if (loading) {
      return;
    }
    // TODO: Implement the logic to get the mail chimp call
    setLoading(true);
    const response: any = await addLocalSEOTag();
    setLoading(false);
    setError({
        status: response?.status,
        message: response?.message
      });
    setTimeout(() => {
      setError({
          status: '',
          message: ''
        });
    }, 5000);
  };


  const scanForCitations = async (id = null) => {
    const locationId =
      id || googleBusinessProfileState?.selectedBusinessProfile?.locationId;

    setBLLocationId(String(locationId));
    // Store the selected business profile in CitationProvider
    if (googleBusinessProfileState?.selectedBusinessProfile) {
      const {
        accountId,
        name,
        locationId: profileLocationId
      } = googleBusinessProfileState.selectedBusinessProfile;
      setSelectedBusinessProfile({
        accountId: accountId || '',
        name: name || '',
        locationId: profileLocationId || ''
      });
    }

    // If location type is new, set the selected business profile to empty
    if(locationType === 'new'){
      setSelectedBusinessProfile({
        accountId: '',
        name: '',
        locationId: ''
      });
    }

    if (!locationId) {
      setError({
          status: 'error',
          message: 'Please select a location before scanning citations.'
        });
      return;
    }

    try {
      setLoading(true);

      const campaignDetails: any = await scanCitations({ locationId });

      if (campaignDetails?.status === 'success') {
        const [createdCampaign] = campaignDetails?.response?.campaigns || [];

        if (createdCampaign?.campaignId) {
          const campaignId = String(createdCampaign.campaignId);

          // Set a minimal campaignDetails object so automatic persistence works
          setCampaignDetails({
            campaign_id: parseInt(campaignId),
            lookup_status: 'processing',
            referenceNap: {
              source: '',
              nap: {
                name: '',
                address: '',
                phone: '',
                postcode: ''
              }
            },
            existingCitations: { citations: [] },
            availableCitations: { citations: [] },
            availablePublishers: {}
          });

          // Persist selectedBusinessProfile to localStorage now that we have campaignId
          // setTimeout(() => {
          //   persistStateToStorage();
          // }, 0);

          // return;
          setShowCategoryField(false);
          navigate('/local-seo/citations/scanning', {
            state: { campaignId: createdCampaign.campaignId, locationId: locationId }
          });
        } else {
          setError({
              status: 'error',
              message: 'Failed to create campaign. Please try again.'
            });
        }
      } else {
        setError({
            status: campaignDetails?.status || 'error',
            message:
              campaignDetails?.message ||
              'Failed to scan citations. Please try again.'
          });
      }
      return campaignDetails;
    } catch (error) {
      setError({
          status: 'error',
          message:
            error instanceof Error
              ? error.message
              : 'An unexpected error occurred while scanning citations.'
        });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleAddLocationModalClose = () => {
    setOpenAddLocationDetailsModal(false);
  };

  const getLocationDetailByPlaceID = async (id, isGMB = false) => {
    const payload = { place_id: id };
    const response: any = await getLocationDetail(payload);
    const info = response?.data;
    await checkLocationInfoFields(info);
  };

  const checkLocationInfoFields = async (locationInfo = null) => {
    const filtered = Country.getAllCountries()?.filter((c) =>
      allowedCountries?.includes(c.isoCode)
    );
    const countries = filtered?.map((c) => ({
      label: c?.name,
      value: c?.isoCode,
      iso3: iso2ToIso3[c.isoCode] || ''
    }));

    // ✅ Deep clone so we can safely modify without mutating frozen Redux state
    let info: any = locationInfo
      ? JSON.parse(JSON.stringify(locationInfo))
      : {};

    const isServiceArea =
      !info?.serviceArea ||
      !info?.serviceArea?.businessType ||
      info?.serviceArea?.businessType == 'BUSINESS_TYPE_UNSPECIFIED';
    setShowServiceArea(isServiceArea);
    const categoryName = info?.categories?.displayName || null;
    let matchedCountry = countries?.find(
      (c) => c.value === info?.storefrontAddress?.regionCode
    );

    const hasValue = (val: any) =>
      val !== null && val !== '' && val !== undefined;

    const checkAllPresent = (infoData: any, countryData: any) => {
      const requiredFields = [
        infoData?.storefrontAddress?.addressLines?.join(', '), // address
        infoData?.storefrontAddress?.locality, // city
        infoData?.storefrontAddress?.administrativeArea, // state
        countryData?.iso3, // country
        infoData?.storefrontAddress?.postalCode, // postal code
        infoData?.latlng?.latitude, // latitude
        infoData?.latlng?.longitude, // longitude
        infoData?.title, // business name
        //infoData?.phoneNumbers?.[0], // phone number
        infoData?.mapsUri, // map URL
        infoData?.websiteUri // website URL
      ];
      return requiredFields.every(hasValue);
    };

    let allPresent = checkAllPresent(info, matchedCountry);

    if (!allPresent) {
      if (locationInfo) {
        info.storefrontAddress = info?.storefrontAddress || {};
        info.latlng = info?.latlng || {};
        if (isServiceArea) {
          setShowServiceArea(!locationInfo?.serviceArea);
        }
        if (
          !info?.storefrontAddress?.addressLines?.[0] &&
          locationInfo?.address
        ) {
          info.storefrontAddress.addressLines = [locationInfo.address];
        }
        if (!info?.storefrontAddress?.locality && locationInfo?.city) {
          info.storefrontAddress.locality = locationInfo?.city;
        }
        if (
          !info?.storefrontAddress?.administrativeArea &&
          locationInfo?.state
        ) {
          info.storefrontAddress.administrativeArea = locationInfo?.state;
        }
        if (!matchedCountry && locationInfo?.country) {
          matchedCountry = countries.find(
            (c) =>
              c.label.toLowerCase() === locationInfo?.country?.toLowerCase()
          );
        }
        if (!info?.storefrontAddress?.postalCode && locationInfo?.postalCode) {
          info.storefrontAddress.postalCode = locationInfo?.postalCode;
        }
        if (!info?.latlng?.latitude && locationInfo?.lattitude) {
          info.latlng.latitude = locationInfo?.lattitude;
        }
        if (!info?.latlng?.longitude && locationInfo?.longitude) {
          info.latlng.longitude = locationInfo?.longitude;
        }
        if (!info?.title && locationInfo?.businessName) {
          info.title = locationInfo?.businessName;
        }
        if (!info?.phoneNumbers?.[0] && locationInfo?.phoneNumber) {
          info.phoneNumbers = [locationInfo?.phoneNumber];
        }
        if (!info?.mapsUri && locationInfo.mapUrl) {
          info.mapsUri = locationInfo?.mapUrl;
        }
        if (!info?.websiteUri && locationInfo.websiteUrl) {
          info.websiteUri = locationInfo?.websiteUrl;
        }
      }

      allPresent = checkAllPresent(info, matchedCountry);
    }

    if (matchedCountry?.iso3 && allPresent) {
      const preparedData = {
        locationName: info?.title || '',
        businessCategory: businessCategory || null,
        businessDescription: info?.description || '',
        websiteUrl: info?.websiteUri || '',
        phoneNumber: info?.phoneNumbers?.[0] || '',
        businessLongitude: info?.latlng?.longitude ?? '',
        businessLatitude: info?.latlng?.latitude ?? '',
        businessMapUrl: info?.mapsUri || '',
        addressLine1: info?.storefrontAddress?.addressLines?.[0] || '',
        addressLine2: info?.storefrontAddress?.addressLines?.[1] || '',
        state: {
          name: info?.storefrontAddress?.administrativeArea || '',
          value: info?.storefrontAddress?.administrativeArea || ''
        },
        country: matchedCountry,
        city: {
          name: info?.storefrontAddress?.locality || '',
          value: info?.storefrontAddress?.locality || ''
        },
        zipcode: info?.storefrontAddress?.postalCode || '',
        isServiceArea:
          !info?.serviceArea ||
          !info?.serviceArea?.businessType ||
          info?.serviceArea?.businessType === 'BUSINESS_TYPE_UNSPECIFIED'
            ? 'yes'
            : 'no'
      };

      setAllFieldData(preparedData);

      setShowAdditionStep(false);
      setCountrySelected(matchedCountry);
      getBusinessCategoryDetails(matchedCountry?.iso3, categoryName);
    } else if (matchedCountry?.iso3 && categoryName) {
      getBusinessCategoryDetails(matchedCountry?.iso3, categoryName);
      setShowAdditionStep(true);
    } else {
      setShowAdditionStep(true);
    }
    setCheckingFieldProgress(false);
  };

  const getBusinessCategoryDetails = async (code, categoryName = null) => {
    if (!code) {
      setBusinessCategories([]); // reset categories if no country
      setShowCategory(true);
      return;
    }
    try {
      const payload: any = { country: code };
      if (categoryName) {
        payload.categoryName = categoryName;
      }
      const response: any = await getBusinessCategory(payload);
      setShowCategory(!response?.selectedCategoryId);
      setBusinessCategories(response?.category?.items || []);
      setSelectedCategoryId(response?.selectedCategoryId || null);
      dispatch(setAutoSelectedCategoryId(response?.selectedCategoryId || null));
    } catch (error) {
      setBusinessCategories([]);
      setShowCategory(true);
    }
  };

  const getLocationId = async () => {
    setLoading(true);
    const isShowInfoModal =
      !selectedOption && inputValue && !businessProfileLoading;

    if (selectedOption) {
      dispatch(setBusinessNameSelectedValue(selectedOption));
    }
    if (isShowInfoModal || showAdditionStep) {
      setOpenAddLocationDetailsModal(true);
      setLoading(false);
    } else {
      setShowCategoryField(true);

      if (selectedCategoryId || businessCategory?.id) {
        const response: any = await callCreateLocation(allFieldData);
        if (response?.status === 'success') {
          scanForCitations(response?.locationId);
        } else {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    }
    // scanForCitations();
  };

  const callCreateLocation = async (data: any) => {
    try {
      const info: any = {};
      const longitude = data?.businessLongitude ?? info?.latlng?.longitude;
      const latitude = data?.businessLatitude ?? info?.latlng?.latitude;
      const isServiceArea =
        !info?.serviceArea ||
        !info?.serviceArea?.businessType ||
        info?.serviceArea?.businessType == 'BUSINESS_TYPE_UNSPECIFIED';
      let payload: any;
      const description =
        data?.businessDescription && data?.businessDescription?.trim() !== ''
          ? data?.businessDescription
          : `${data?.locationName || ''}, ${
              data?.businessCategory?.name || ''
            }` || '';
      payload = {
        business_name: data?.locationName || '',
        country: data?.country?.iso3 || '',
        address: {
          address1: data?.addressLine1 || '',
          address2: data?.addressLine2 || '',
          state:
            info?.storefrontAddress?.administrativeArea ??
            (typeof data?.state === 'string'
              ? data?.state
              : data?.state?.name || ''),
          region_code:
            info?.storefrontAddress?.regionCode ?? (data?.country?.value || ''),
          city:
            info?.storefrontAddress?.locality ??
            (data?.country?.iso3 === 'GBR'
              ? typeof data?.state === 'string'
                ? data?.state
                : data?.state?.name
              : (typeof data?.city === 'string'
                  ? data.city
                  : data.city?.name) ||
                (typeof data?.state === 'string'
                  ? data?.state
                  : data?.state?.name) ||
                ''),
          postcode: data?.zipcode || ''
        },
        business_category_id:
          selectedCategoryId ||
          businessCategory?.id ||
          data?.businessCategory?.id ||
          null,
        description: description || '',
        urls: data?.websiteUrl || '',
        is_service_area_business: isServiceArea
          ? data?.isServiceArea === 'yes'
          : true
      };

      if (data?.phoneNumber) {
        payload.telephone = data?.phoneNumber;
      }

      const response: any = await createLocation(payload);
      return response;
    } catch (error) {
      return null;
    }
  };

  const deduplicateById = (arr = []) =>
    Array.from(new Map(arr.map((item) => [item.id, item])).values());

  // filtered categories logic remains the same
  const filteredCategories = countrySelected
    ? deduplicateById(businessCategories || [])
    : [];

  return (
    <>
      <Box sx={CitationBuilderSx.outerContainer}>
        <Box sx={CitationBuilderSx.innerContainer}>
            <>
              <Typography sx={CitationBuilderSx.titleTypography}>
                Build & Manage Your Online Business Listings
              </Typography>
              <Typography sx={CitationBuilderSx.descriptionTypography}>
                Easily build, manage, and update your business citations across
                important online directories and platforms from one central
                place.
              </Typography>
              <Box sx={CitationBuilderSx.imageContainer}>
                <Box
                  component="img"
                  src="/static/images/icons/local-seo/citation-bg-new.png"
                  alt="image"
                  sx={CitationBuilderSx.image}
                />
              </Box>
            </>

          <Box sx={CitationBuilderSx.radioContainer}>
            <RadioGroup
              row
              value={locationType}
              onChange={handleLocationTypeChange}
              sx={CitationBuilderSx.radioGroup}
            >
              {googleBusinessProfileState?.allBusinessProfiles?.length > 0 && (
                <>
                  <FormControlLabel
                    value="existing"
                    control={
                      <Radio size="small" sx={CitationBuilderSx.radio} />
                    }
                    label={
                      <Typography sx={CitationBuilderSx.radioLabel}>
                        Use existing
                      </Typography>
                    }
                    sx={CitationBuilderSx.formControlLabel}
                  />
                  <FormControlLabel
                    value="new"
                    control={
                      <Radio size="small" sx={CitationBuilderSx.radio} />
                    }
                    label={
                      <Typography sx={CitationBuilderSx.radioLabel}>
                        Select New Location
                      </Typography>
                    }
                    sx={CitationBuilderSx.formControlLabel}
                  />
                </>
              )}
            </RadioGroup>
          </Box>

          <Box sx={CitationBuilderSx.actionContainer}>
            {locationType === 'existing' ? (
              <Box sx={CitationBuilderSx.dropdownContainer}>
                <GoogleBusinessProfileSelector />
              </Box>
            ) : (
              <Box sx={{ width: '100%' }}>
                {googleBusinessProfileState?.allBusinessProfiles?.length === 0 && (
                  <>
                    <Typography sx={
                      {
                        fontFamily: 'Inter',
                        fontWeight: 500,
                        fontSize: 'var(--font-size-14)',
                        lineHeight: 'var(--line-height-20)',
                        marginBottom: '6px',
                        color: 'var(--color-text-secondary)'
                      }
                    }>
                      Enter Google Business Profile Name
                    </Typography>
                  </>
                )}
                <Box sx={CitationBuilderSx.businessProfileContainer}>
                  <BusinessProfileAutocomplete />
                </Box>
              </Box>
            )}
            <Box sx={CitationBuilderSx.buttonContainer}>
              <Button
                variant="contained"
                fullWidth={isMobile}
                sx={CitationBuilderSx.button}
                onClick={() => {
                  if (locationType === 'new') {
                    getLocationId();
                  } else {
                    scanForCitations();
                  }
                }}
                disabled={
                  loading ||
                  (showCategoryField &&
                    !selectedCategoryId &&
                    !businessCategory?.id)
                }
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Scan Citations Now'
                )}
              </Button>
            </Box>
          </Box>
          {showCategoryField && locationType === 'new' && (
            <Box sx={CitationBuilderSx.businessProfileContainer}>
              <Box sx={LocationDetailsModalSx?.tooltipBoxSx}>
                <Typography sx={{ marginTop: '8px', ...labelTypographySx }}>
                  {MapRankTexts?.selectCategory}
                </Typography>
                <CustomTooltip
                  title={
                    <Box>
                      <Typography
                        sx={keywordResearchDashBoardCss?.customTollTipText}
                      >
                        {LocationDetailsModalTexts?.categoryTooltipText}
                      </Typography>
                    </Box>
                  }
                  placement="bottom"
                  arrow
                  PopperProps={{
                    style: { zIndex: 1600 }
                  }}
                >
                  <HelpIcon
                    sx={{
                      marginBottom: '6px',
                      marginTop: '8px',
                      ...KeywordOverviewResultCardsCss?.helpIcon
                    }}
                  />
                </CustomTooltip>
              </Box>
              <Autocomplete
                options={filteredCategories}
                open={open}
                onOpen={() => {
                  if (categoryInputValue.length >= 3) setOpen(true);
                }}
                onClose={() => setOpen(false)}
                onInputChange={(_, value, reason) => {
                  setCategoryInputValue(value);
                  setOpen(true);

                  if (reason === 'clear' || value.trim() === '') {
                    setBusinessCategory(null);
                  }
                }}
                getOptionLabel={(option) => option?.name || ''}
                isOptionEqualToValue={(option, value) =>
                  option?.id === value?.id
                }
                value={businessCategory}
                onChange={(_e, newValue) => setBusinessCategory(newValue)}
                loading={!businessCategories && !!countrySelected}
                disableClearable
                popupIcon={null}
                disabled={!countrySelected}
                filterOptions={(
                  options,
                  { inputValue: categoryInputValue }
                ) => {
                  const query = normalize(categoryInputValue || '');
                  if (query.length < 3) return [];

                  const queryWords = query.split(' ').filter(Boolean);

                  const filtered = (options || []).filter((option) => {
                    const name = normalize(option?.name || '');
                    return (
                      name.includes(query) ||
                      queryWords.every((word) =>
                        new RegExp(`\\b${word}\\b`, 'i').test(name)
                      )
                    );
                  });

                  return filtered.sort((a, b) => {
                    const aName = normalize(a?.name || '');
                    const bName = normalize(b?.name || '');
                    const aIndex = aName.indexOf(query);
                    const bIndex = bName.indexOf(query);
                    return aIndex === bIndex
                      ? aName.localeCompare(bName)
                      : aIndex - bIndex;
                  });
                }}
                noOptionsText={
                  !countrySelected
                    ? 'Select a country first'
                    : categoryInputValue.length < 3
                    ? 'Type at least 3 characters'
                    : 'No results found'
                }
                sx={{
                  ...BusinessProfileAutocompleteStyles.autoCompleteSx,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: inputValue ? 'var(--color-primary)' : 'var(--color-border-light2)'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: inputValue ? 'var(--color-primary)' : 'var(--color-text-light-grey)'
                  },
                  '& .MuiOutlinedInput-root': {
                    color: inputValue ? 'var(--color-primary)' : 'rgba(0, 0, 0, 0.40)',
                    fontWeight: 400
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Enter Business Category"
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      ...params.InputProps,
                      readOnly: !countrySelected,
                      endAdornment: (
                        <>
                          {!businessCategories && !!countrySelected ? (
                            <CircularProgress size={16} />
                          ) : null}
                        </>
                      )
                    }}
                    sx={BusinessProfileAutocompleteStyles.textFieldSx}
                  />
                )}
                renderOption={(props, option) => (
                  <ListItem
                    {...props}
                    key={option.id}
                    sx={{
                      ...MapRankCss.autoCompleteListItem,
                      color: 'var(--color-text-medium-grey)',
                      fontSize: 'var(--font-size-14)',
                      paddingY: '6px'
                    }}
                  >
                    <Typography sx={MapRankCss.autoCompleteName}>
                      {option?.name}
                    </Typography>
                  </ListItem>
                )}
                PopperComponent={(props: any) => {
                  const anchorEl = props.anchorEl as HTMLElement;
                  return (
                    <Popper
                      {...props}
                      placement="bottom-start"
                      style={{
                        width: anchorEl?.clientWidth || undefined,
                        zIndex: 1300
                      }}
                    />
                  );
                }}
              />
            </Box>
          )}
        </Box>
      </Box>
      <CommonModal
        open={openAddLocationDetailsModal}
        handCloseModal={() => {
          setOpenAddLocationDetailsModal(false);
        }}
        title={''}
        closebtn={false}
        isAgencyModal={true}
        isInfoModal={true}
        isFreeDowngrade={true}
        isDynamicWidth={'937px'}
      >
        <AddLocationDetailsModal
          handleClose={handleAddLocationModalClose}
          callCreateLocation={callCreateLocation}
          scanForCitations={scanForCitations}
        />
      </CommonModal>
    </>
  );
};
